import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const RFQCreate = () => {
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [procurementItems, setProcurementItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const units = ['pcs', 'kg', 'lbs', 'ft', 'm', 'sqft', 'sqm', 'gal', 'L', 'box', 'roll', 'sheet'];

  const [formData, setFormData] = useState({
    rfqNumber: '',
    title: '',
    description: '',
    project: '',
    category: '',
    items: [],
    vendors: [],
    submissionDeadline: null,
    deliveryLocation: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    evaluationCriteria: {
      price: 40,
      quality: 30,
      delivery: 20,
      experience: 10
    },
    terms: '',
    notes: ''
  });

  const [newItem, setNewItem] = useState({
    item: '',
    description: '',
    quantity: 1,
    unit: 'pcs',
    specifications: ''
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [vendorsRes, itemsRes, companiesRes, projectsRes] = await Promise.all([
          api.get('/vendors?status=active'),
          api.get('/procurement-items'),
          api.get('/companies?limit=100'),
          api.get('/projects?limit=100')
        ]);
        setVendors(vendorsRes.data || []);
        setProcurementItems(itemsRes.data || []);
        const list = (companiesRes.data && companiesRes.data.data) || companiesRes.data || [];
        setCompanies(Array.isArray(list) ? list : []);
        const projectsListRaw = (projectsRes.data && (projectsRes.data.data || projectsRes.data.projects)) || projectsRes.data || [];
        setProjects(Array.isArray(projectsListRaw) ? projectsListRaw : []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load vendors/items/companies/projects');
      }
    };
    loadData();
  }, []);

  const effectiveVendors = selectedCompany
    ? vendors.filter((v) => (v.companyName || '').toLowerCase() === selectedCompany.toLowerCase())
    : vendors;

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddItem = () => {
    if (newItem.item && newItem.quantity > 0) {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, { ...newItem }]
      }));
      setNewItem({ item: '', description: '', quantity: 1, unit: 'pcs', specifications: '' });
    }
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = { ...formData };
      // Basic client-side required field validation
      const missing = [];
      if (!payload.title) missing.push('Title');
      if (!payload.description) missing.push('Description');
      if (!payload.project) missing.push('Project');
      if (!payload.category) missing.push('Category');
      if (!payload.submissionDeadline) missing.push('Submission Deadline');
      if (missing.length) {
        setError(`Please complete required fields: ${missing.join(', ')}`);
        return;
      }
      if (payload.project) {
        payload.projectId = payload.project;
        delete payload.project;
      }
      await api.post('/rfqs', payload);
      setSuccess('RFQ created successfully');
      navigate('/rfqs');
    } catch (err) {
      console.error('Error saving RFQ:', err);
      const resp = err?.response?.data;
      const baseMsg = resp?.message || err.message || 'Failed to save RFQ';
      if (resp?.errors) {
        const details = Object.values(resp.errors)
          .map((e) => e?.message || '')
          .filter(Boolean)
          .join('; ');
        setError(details ? `${baseMsg}: ${details}` : baseMsg);
      } else {
        setError(baseMsg);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Create RFQ</Typography>
        <Button variant="outlined" onClick={() => navigate('/rfqs')}>Back to RFQs</Button>
      </Box>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RFQ Number"
                value={formData.rfqNumber}
                onChange={(e) => handleInputChange('rfqNumber', e.target.value)}
                placeholder="Auto-generated if empty"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={formData.project}
                  label="Project"
                  onChange={(e) => handleInputChange('project', e.target.value)}
                >
                  {projects.map((p) => (
                    <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  {['Materials', 'Equipment', 'Services', 'Subcontractor', 'Supplies', 'Other'].map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Submission Deadline"
                value={formData.submissionDeadline}
                onChange={(date) => handleInputChange('submissionDeadline', date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Delivery Location</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.deliveryLocation.street}
                onChange={(e) => handleInputChange('deliveryLocation.street', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.deliveryLocation.city}
                onChange={(e) => handleInputChange('deliveryLocation.city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.deliveryLocation.state}
                onChange={(e) => handleInputChange('deliveryLocation.state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.deliveryLocation.zipCode}
                onChange={(e) => handleInputChange('deliveryLocation.zipCode', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.name || ''}
                value={companies.find((c) => c.name === selectedCompany) || null}
                onChange={(event, newValue) => setSelectedCompany(newValue?.name || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Company Name" placeholder="Select company to filter vendors" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Items</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={procurementItems}
                getOptionLabel={(option) => option.name || ''}
                value={procurementItems.find(item => item._id === newItem.item) || null}
                onChange={(event, newValue) => {
                  setNewItem(prev => ({
                    ...prev,
                    item: newValue?.name || '',
                    description: newValue?.description || ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Item" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={newItem.quantity}
                onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={newItem.unit}
                  label="Unit"
                  onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                >
                  {units.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Specifications"
                value={newItem.specifications}
                onChange={(e) => setNewItem(prev => ({ ...prev, specifications: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button fullWidth variant="contained" onClick={handleAddItem} sx={{ height: '56px' }}>Add</Button>
            </Grid>

            <Grid item xs={12}>
              {formData.items.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Specifications</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((it, index) => (
                        <TableRow key={index}>
                          <TableCell>{it.item}</TableCell>
                          <TableCell>{it.description}</TableCell>
                          <TableCell>{it.quantity}</TableCell>
                          <TableCell>{it.unit}</TableCell>
                          <TableCell>{it.specifications}</TableCell>
                          <TableCell>
                            <Button color="error" size="small" onClick={() => handleRemoveItem(index)}>Remove</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                  No items added yet
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={effectiveVendors}
                getOptionLabel={(option) => option.name}
                value={effectiveVendors.filter(v => formData.vendors.includes(v._id))}
                onChange={(event, newValue) => {
                  handleInputChange('vendors', newValue.map(v => v._id));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Vendors" placeholder="Choose vendors to send RFQ" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Evaluation Criteria (Total: 100%)</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price Weight (%)"
                type="number"
                value={formData.evaluationCriteria.price}
                onChange={(e) => handleInputChange('evaluationCriteria.price', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quality Weight (%)"
                type="number"
                value={formData.evaluationCriteria.quality}
                onChange={(e) => handleInputChange('evaluationCriteria.quality', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Delivery Weight (%)"
                type="number"
                value={formData.evaluationCriteria.delivery}
                onChange={(e) => handleInputChange('evaluationCriteria.delivery', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experience Weight (%)"
                type="number"
                value={formData.evaluationCriteria.experience}
                onChange={(e) => handleInputChange('evaluationCriteria.experience', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Total: {Object.values(formData.evaluationCriteria).reduce((sum, val) => sum + (val || 0), 0)}%
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Terms"
                multiline
                rows={2}
                value={formData.terms}
                onChange={(e) => handleInputChange('terms', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" sx={{ mr: 2 }} onClick={() => navigate('/rfqs')}>Cancel</Button>
              <Button variant="contained" onClick={handleSubmit}>Create RFQ</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RFQCreate;